import { usePopover } from 'minimal-shared/hooks';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import { styled } from '@mui/material/styles';

import { RouterLink } from '../../../routes/components';

import { fDate } from '../../../utils/format-time';
import { fShortenNumber } from '../../../utils/format-number';
import { Image } from '../../../Components/image';
import Iconify from '../../../Components/iconify';
import { CustomPopover } from '../../../Components/custom-popover';
import EditPost from './post-edit';
import YouTubeModal from './YoutubeModal';
import axios from 'axios';

// ----------------------------------------------------------------------
const RedBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#87041a',
  },
}));
export function PostItemHorizontal({
  sx,
  post,
  editHref,
  detailsHref,
  isSuperAdmin,
  onEdit,
  onDelete,
  tags,
  categories,
  newVideo,
  ...other
}) {
  const [edit, setEdit] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const menuActions = usePopover();

  useEffect(() => {
    setIsNew(newVideo);
  }, [newVideo]);

  function handleEdit(formData) {
    axios.post(`/help/updateVideo`, formData).then((res) => {
      onEdit(res.data);
    });
  }

  function handleDelete(postId) {
    axios.delete(`/help/deleteVideo/${postId}`).then((res) => {
      onDelete(postId);
    });
  }

  function increaseWatch(postId) {
    axios.get(`/help/increaseWatch/${postId}`).then((res) => {});
    post.totalViews += 1;
  }
  const renderMenuActions = () => (
    <CustomPopover
      open={menuActions.open}
      anchorEl={menuActions.anchorEl}
      onClose={menuActions.onClose}
      slotProps={{ arrow: { placement: 'bottom-center' } }}
    >
      <MenuList>
        <li>
          <MenuItem
            component={RouterLink}
            href={editHref}
            onClick={() => {
              menuActions.onClose();
              setEdit(true);
            }}
          >
            <Iconify icon="solar:pen-bold" />
            Edit
          </MenuItem>
        </li>

        <MenuItem
          onClick={() => {
            menuActions.onClose();
            handleDelete(post._id);
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon="solar:trash-bin-trash-bold" />
          Delete
        </MenuItem>
      </MenuList>
    </CustomPopover>
  );

  return (
    <>
      <RedBadge badgeContent={'New'} invisible={!isNew}>
        <Card
          sx={[
            {
              display: 'flex',
              '&:hover': {
                transform: 'scale(1.005)',
                transition: 'transform 0.3s ease-in-out',
                textDecoration: 'none',
              },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          {...other}
        >
          <Stack
            spacing={1}
            sx={[
              (theme) => ({
                flexGrow: 1,
                p: theme.spacing(3, 3, 2, 3),
              }),
            ]}
          >
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
              }}
            >
              {post.tags
                ? post.tags.map((tag, i) =>
                    i < 3 ? (
                      <Chip
                        label={tag}
                        key={tag}
                        size="small"
                        style={{ backgroundColor: 'primary', color: 'textPrimary', mb: 1 }}
                      ></Chip>
                    ) : null,
                  )
                : null}
              {post.publish === 'draft' && (
                <Chip label="Draft" sx={{ backgroundColor: 'orange', position: 'absolute', right: 0, zIndex: 100 }} />
              )}
              <Box component="span" sx={{ typography: 'caption', color: 'text.disabled' }}>
                {fDate(post.createdAt)}
              </Box>
            </Box>

            <Stack spacing={1} sx={{ flexGrow: 1, maxWidth: '350x' }}>
              <Typography
                color="inherit"
                variant="subtitle2"
                sx={[
                  (theme) => ({
                    ...theme.mixins.maxLine({ line: 2 }),
                    maxWidth: '300px',
                  }),
                ]}
              >
                {post.title}
              </Typography>
              <Tooltip title={post.description}>
                <Typography
                  variant="body2"
                  sx={[
                    (theme) => ({
                      ...theme.mixins.maxLine({ line: 2 }),
                      color: 'text.secondary',
                      maxWidth: '300px',
                    }),
                  ]}
                >
                  {post.description}
                </Typography>
              </Tooltip>
            </Stack>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isSuperAdmin && (
                <IconButton color={menuActions.open ? 'inherit' : 'default'} onClick={menuActions.onOpen}>
                  <Iconify icon="eva:more-horizontal-fill" />
                </IconButton>
              )}
              <Button
                onClick={() => {
                  setVideoOpen(true);
                  increaseWatch(post._id);
                  setIsNew(false);
                }}
              >
                Watch
              </Button>

              <Box
                sx={{
                  gap: 1.5,
                  flexGrow: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  typography: 'caption',
                  color: 'text.disabled',
                  justifyContent: 'flex-end',
                }}
              >
                <Box sx={{ gap: 0.5, display: 'flex', alignItems: 'center' }}>
                  <Iconify icon="solar:eye-bold" width={16} />
                  {fShortenNumber(post.totalViews)}
                </Box>
              </Box>
            </Box>
          </Stack>

          <Box
            sx={{
              p: 1,
              width: 180,
              height: 240,
              flexShrink: 0,
              position: 'relative',
              display: { xs: 'none', sm: 'block' },
              border: 'red',
            }}
          >
            <Image
              visibleByDefault={true}
              alt={post.title}
              src={post.coverImage}
              sx={{ height: 1, borderRadius: 1.5 }}
            />
          </Box>
        </Card>
        {renderMenuActions()}
        <EditPost
          open={edit}
          onClose={(data, formData) => {
            setEdit(false);
            handleEdit({ _id: post._id, ...formData });
          }}
          post={post}
          tags={tags}
          categories={categories}
        />
        <YouTubeModal open={videoOpen} onClose={() => setVideoOpen(false)} videoUrl={post.videoUrl} post={post} />
      </RedBadge>
    </>
  );
}
